import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Placement() {
  const { ctx } = useWorkspace();
  const { has } = useAuth();
  const pid = ctx.project?.id;
  const canManage = has("PLACEMENT.MANAGE");
  const canExport = has("REPORT.PLACEMENT.EXPORT");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback((params) =>
    api.get("/placement/queue", { params: { ...params, ...(pid ? { project_id: pid } : {}) } }).then((r) => r.data), [pid]);

  const columns = [
    { key: "material_number", label: "Material Number", render: (r) => <span className="font-mono text-xs font-semibold text-slate-800">{r.material_number ?? "—"}</span> },
    { key: "serial", label: "Serial", render: (r) => <span className="font-mono text-xs">{r.serial || "—"}</span> },
    { key: "entry_number", label: "Data Entry No", render: (r) => <span className="font-mono text-xs">{r.entry_number}</span> },
    { key: "data_entry_status", label: "Data Entry Status", render: (r) => <Badge className={r.data_entry_status === "COMPLETED" ? "bg-emerald-600" : "bg-amber-500"}>{r.data_entry_status}</Badge> },
    { key: "installation_date", label: "Installation Date", render: (r) => r.installation_date || "—" },
    { key: "fg_status", label: "Push to FG Status", render: (r) => r.fg_status === "Yes"
        ? <Badge className="bg-teal-600" data-testid={`fg-yes-${r.material_number}`}>Yes</Badge>
        : <Badge variant="outline" className="border-red-300 text-red-600" data-testid={`fg-no-${r.material_number}`}>No</Badge> },
    { key: "fg_push_date", label: "Push to FG Date", render: (r) => r.fg_push_date || "—" },
  ];

  const filters = [{ key: "fg_status", label: "Push to FG", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] }];

  const downloadTemplate = async () => {
    const r = await api.get("/placement/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Push_to_FG_Template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doUpload = async () => {
    if (!file) { toast.error("Choose the filled template Excel"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (pid) fd.append("project_id", pid);
      const { data } = await api.post("/placement/upload", fd);
      setUploadResult(data);
      setFile(null);
      setRefreshKey((k) => k + 1);
      if (data.updated > 0) toast.success(`${data.updated} material(s) marked Push to FG = Yes`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const requestExport = async () => {
    try {
      await api.post("/reports/exports", { report_type: "FG_PUSH_PENDING", filters: { ...(pid ? { project_id: pid } : {}) } });
      toast.success("Export queued — track it in Download Centre");
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
  };

  return (
    <div>
      <PageHeader title="Material Punching Status" subtitle="Meter data-entry queue — Push to FG tracking" />
      <DataTable testid="placement-queue" columns={columns} load={load} filters={filters}
        refreshKey={`${pid || "all"}-${refreshKey}`} searchPlaceholder="Search material serial / number..."
        toolbar={<>
          <Button data-testid="placement-template" size="sm" variant="outline" onClick={downloadTemplate}><Download className="mr-1 h-4 w-4" />Template</Button>
          {canManage && <Button data-testid="placement-upload" size="sm" variant="outline" onClick={() => { setUploadResult(null); setUploadOpen(true); }}><Upload className="mr-1 h-4 w-4" />Upload Push to FG</Button>}
          {canExport && <Button data-testid="placement-export" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={requestExport}><FileSpreadsheet className="mr-1 h-4 w-4" />Export Pending (No)</Button>}
        </>}
      />
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="placement-upload-dialog">
          <DialogHeader><DialogTitle>Upload Push to FG</DialogTitle>
            <DialogDescription>Excel template with columns: Material Number, Push to FG date. Matched materials are marked Push to FG = Yes.</DialogDescription>
          </DialogHeader>
          <input data-testid="placement-file" type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          {uploadResult && (
            <div className="space-y-2 text-sm">
              <p className="font-medium text-emerald-700" data-testid="placement-upload-summary">Updated {uploadResult.updated} material(s){uploadResult.failed ? `, ${uploadResult.failed} row(s) failed` : ""}.</p>
              {(uploadResult.errors || []).map((er, i) => (
                <div key={i} className="rounded-md border border-red-100 bg-red-50/50 p-2 text-xs" data-testid={`placement-upload-err-${i}`}>
                  <span className="font-mono font-semibold">{er.material_number}</span> — <span className="text-red-600">{er.reason}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Close</Button>
            <Button data-testid="placement-upload-submit" disabled={busy || !file} onClick={doUpload} className="bg-teal-700 hover:bg-teal-800">{busy ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
